import { FC, useMemo } from 'react';

import SectionContainer from '@common/components/SectionContainer';
import { Routes } from '@common/constants';
import { useCheckForArrivalDate } from '@common/hooks';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useSpaSearchComplete } from '@features/JourneyPickerV2/useSpaSearchComplete';
import { useTranslations } from '@lang';
import { useRouter } from 'shared-routing';

import { useGetSpaCategories } from '@features/spa/hooks/useGetSpaCategories';

import { SpaCategoryListTemplate, SpaListTemplate } from 'voyager';

import { useGetSpaSettings } from '@features/spa/hooks/useGetSpaSettings';
import { mapCategoriesToCards } from '@features/spa/utils/mapCategoriesToCards';

import { useSpaEntityFilter } from '@features/spa/hooks/useSpaEntityFilter';
import { buildCategoryOptions } from '@features/spa/utils/buildCategoryOptions';
import { useSpaDaysList } from './hooks/useSpaDaysList';
import { SpaDaysV2Props } from './SpaDaysV2.types';

export const SpaDaysV2: FC<SpaDaysV2Props> = ({
  property,
  journeyParams,
  categoryId = null,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.SPA_DAY_LIST,
  });

  const filter = useSpaEntityFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
  });

  const handleSearchComplete = useSpaSearchComplete();

  const { listItems, isFetching } = useSpaDaysList({
    propertyId: property.id,
    language,
    categoryFilterId: filter.categoryFilterId,
    date: filter.appliedDateString,
  });

  const { data: spaPackagesCategories = [], isFetching: isFetchingCategories } =
    useGetSpaCategories(property.id, 'packages', language);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const isSpaDayCategoryViewVisible = spaSettings?.isSpaDayCategoryViewVisible ?? false;

  const categoryOptions = buildCategoryOptions(
    spaPackagesCategories,
    t('spa.package.allCategories')
  );

  const handleSelectSpaDay = (id: string) => {
    router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id } });
  };

  const handleSelectCategory = (id: string) => {
    router.push({ pathname: Routes.SPA_DAY_CATEGORY_DETAIL, query: { id } });
  };

  const titleLabel = t('category.spaDays');

  const durationOptions = useMemo(() => {
    const seen = new Set<string>();
    return listItems.reduce<{ label: string; value: string }[]>((acc, item) => {
      if (item.durationValue && !seen.has(item.durationValue)) {
        seen.add(item.durationValue);
        acc.push({
          label: t(item.durationValue as Parameters<typeof t>[0]),
          value: item.durationValue,
        });
      }
      return acc;
    }, []);
  }, [listItems, t]);

  return (
    <EntitiesListingWrapper>
      {(!isSpaDayCategoryViewVisible || spaPackagesCategories.length === 0) && (
        <JourneyPickerOuterWrapper>
          <JourneyPickerInnerWrapper>
            <JourneyPickerV2
              dateMode="single"
              dateContext="spa"
              categories={navCategories}
              category="spa"
              onCategoryChange={handleNavCategoryChange}
              onSearchComplete={handleSearchComplete}
            />
          </JourneyPickerInnerWrapper>
        </JourneyPickerOuterWrapper>
      )}
      <SectionContainer>
        {isSpaDayCategoryViewVisible && spaPackagesCategories.length > 0 ? (
          <SpaCategoryListTemplate
            categories={mapCategoriesToCards(spaPackagesCategories, 'spaPackages')}
            onSelectCategory={handleSelectCategory}
            isLoading={isFetchingCategories}
            titleLabel={titleLabel}
            fromPriceLabel={t('common.from')}
            noResultsLabel={t('spa.noResults')}
            noImageLabel={t('spa.noImage')}
          />
        ) : (
          <SpaListTemplate
            items={isFetching ? [] : listItems}
            onSelectItem={handleSelectSpaDay}
            isLoading={isFetching}
            titleLabel={titleLabel}
            fromPriceLabel={t('common.from')}
            allDurationsLabel={t('spa.allDurations')}
            soldOutLabel={t('spa.soldOut')}
            noResultsLabel={t('spa.noResults')}
            noImageLabel={t('spa.noImage')}
            formatGuests={(n) => t('common.numberOfGuests', { guestsAmount: n })}
            durationOptions={durationOptions}
            categoryOptions={categoryOptions}
            selectedCategory={filter.selectedCategory || ''}
            onCategoryChange={filter.handleDirectCategoryChange}
            categoryLabel={t('common.category')}
          />
        )}
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
